import React, { useState, useEffect } from "react";
import { toSafeFixed } from "../../utils/numberUtils";

interface NumericInputProps {
  value: number | string;
  onChange: (value: number) => void;
  placeholder?: string;
  className?: string;
  step?: number;
  min?: number;
  max?: number;
  decimalPlaces?: number;
  allowNegative?: boolean;
  disabled?: boolean;
  name?: string;
  required?: boolean;
}

const NumericInput: React.FC<NumericInputProps> = ({
  value,
  onChange,
  placeholder = "0.00",
  className = "",
  step = 0.01,
  min = 0,
  max,
  decimalPlaces = 2,
  allowNegative = false,
  disabled = false,
  name,
  required = false,
}) => {
  const [inputValue, setInputValue] = useState<string>(
    value ? String(value) : ""
  );
  const [isFocused, setIsFocused] = useState(false);

  // Update local state when prop value changes (only if not focused)
  useEffect(() => {
    if (!isFocused) {
      if (value) {
        const numValue = typeof value === 'number' ? value : parseFloat(String(value));
        if (!isNaN(numValue)) {
          setInputValue(toSafeFixed(numValue, decimalPlaces));
        } else {
          setInputValue("");
        }
      } else {
        setInputValue("");
      }
    }
  }, [value, decimalPlaces, isFocused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    
    // Allow empty input (user clearing the field)
    if (val === "") {
      setInputValue("");
      onChange(0);
      return;
    }

    // Allow minus sign at the start if allowNegative
    if (val === "-" && allowNegative) {
      setInputValue(val);
      return;
    }

    // Only accept numeric input (including decimals)
    if (/^-?\d*\.?\d*$/.test(val)) {
      const numValue = parseFloat(val);
      
      // If it's a valid number, check boundaries before updating
      if (!isNaN(numValue)) {
        // Check if negative and not allowed
        if (numValue < 0 && !allowNegative) {
          return;
        }
        
        // Check min boundary
        if (min !== undefined && numValue < min) {
          return;
        }
        
        // Check max boundary - prevent exceeding max while typing
        if (max !== undefined && numValue > max) {
          setInputValue(toSafeFixed(max, decimalPlaces));
          onChange(max);
          return;
        }
        
        // Valid number, update display and parent
        setInputValue(val);
        onChange(numValue);
      } else {
        // Still allow typing partial numbers (e.g., "1." or "100.")
        setInputValue(val);
      }
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    
    const val = e.target.value;

    // If empty, set to 0
    if (val === "" || val === "-") {
      onChange(0);
      setInputValue("");
      return;
    }

    const numValue = parseFloat(val);

    // If invalid number, reset to 0
    if (isNaN(numValue)) {
      onChange(0);
      setInputValue("");
      return;
    }

    // Check if negative and not allowed
    if (numValue < 0 && !allowNegative) {
      const validValue = 0;
      onChange(validValue);
      setInputValue("");
      return;
    }

    // Check min boundary
    if (min !== undefined && numValue < min) {
      onChange(min);
      setInputValue(toSafeFixed(min, decimalPlaces));
      return;
    }

    // Check max boundary
    if (max !== undefined && numValue > max) {
      onChange(max);
      setInputValue(toSafeFixed(max, decimalPlaces));
      return;
    }

    // Valid number, update and format to specified decimal places
    onChange(numValue);
    setInputValue(toSafeFixed(numValue, decimalPlaces));
  };

  return (
    <input
      type="text"
      name={name}
      value={inputValue}
      onChange={handleChange}
      onFocus={() => setIsFocused(true)}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      required={required}
    />
  );
};

export default NumericInput;
